<template>
  <div class="card shadow-sm p-3">
    <!-- Header -->
    <div class="d-flex align-items-center gap-2 mb-3">
      <span class="material-icons text-success">health_and_safety</span>
      <span class="fw-bold">Cobertura CC — Planilla {{ numero }}</span>
    </div>

    <button
      class="btn btn-primary btn-sm d-flex align-items-center gap-1"
      @click="consultar"
      :disabled="procesando"
    >
      <span v-if="procesando" class="spinner-border spinner-border-sm"></span>
      <span v-else class="material-icons" style="font-size:16px">health_and_safety</span>
      {{ procesando ? 'Consultando...' : 'Consultar Cobertura' }}
    </button>

    <!-- Error -->
    <div v-if="errorMensaje" class="alert alert-danger small mt-3">
      <span class="material-icons me-1" style="font-size:15px;vertical-align:middle">error</span>
      {{ errorMensaje }}
    </div>

    <!-- Resultado -->
    <div v-if="resultado" class="mt-3">
      <hr />

      <!-- Alerta resumen -->
      <div class="alert py-2 small" :class="tieneAlgunaCobertura ? 'alert-success' : 'alert-warning'">
        <span class="material-icons me-1" style="font-size:15px;vertical-align:middle">
          {{ tieneAlgunaCobertura ? 'check_circle' : 'warning' }}
        </span>
        <span v-if="tieneAlgunaCobertura">
          El paciente <strong>{{ resultado.nombreBd }}</strong>
          registra cobertura activa.
        </span>
        <span v-else>
          El paciente <strong>{{ resultado.nombreBd }}</strong>
          no registra cobertura activa en ningún seguro.
        </span>
      </div>

      <!-- Indicador múltiples registros -->
      <div v-if="resultado.paginas?.length > 1"
           class="alert alert-info small py-2 px-3 mb-3 d-flex align-items-center gap-2">
        <span class="material-icons" style="font-size:16px">info</span>
        Se encontraron <strong>{{ resultado.paginas.length }} registros</strong>
        para la cédula <strong>{{ resultado.cedula }}</strong> en el CC.pdf.
      </div>

      <!-- Tarjeta por cada página encontrada -->
      <div
        v-for="(p, idx) in resultado.paginas"
        :key="idx"
        class="card mb-3"
        :class="paginaTieneCobertura(p) ? 'border-success' : 'border-danger'"
      >
        <!-- Header tarjeta -->
        <div
          class="card-header d-flex justify-content-between align-items-center py-2 small"
          :class="paginaTieneCobertura(p)
            ? 'bg-success bg-opacity-10 text-success'
            : 'bg-danger bg-opacity-10 text-danger'"
        >
          <div class="d-flex align-items-center gap-2">
            <span class="material-icons" style="font-size:16px">
              {{ paginaTieneCobertura(p) ? 'check_circle' : 'cancel' }}
            </span>
            <span class="fw-bold">{{ p.nombrePdf ?? resultado.cedula }}</span>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span class="badge bg-light text-dark">
              Pág. {{ p.pagina }} / {{ p.totalPaginas }}
            </span>
            <span class="badge" :class="paginaTieneCobertura(p) ? 'bg-success' : 'bg-danger'">
              {{ paginaTieneCobertura(p) ? 'Con cobertura' : 'Sin cobertura' }}
            </span>
          </div>
        </div>

        <!-- Body tarjeta -->
        <div class="card-body py-2 px-3">
          <!-- Fechas -->
          <div class="row g-2 mb-2 small">
            <div class="col-auto">
              <span class="text-muted fw-bold">F. Cobertura: </span>
              <span>{{ p.fechaCobertura ?? '-' }}</span>
            </div>
            <div class="col-auto">
              <span class="text-muted fw-bold">F. Consulta: </span>
              <span>{{ p.fechaConsulta ?? '-' }}</span>
            </div>
          </div>

          <!-- Tabla seguros -->
          <table class="table table-sm table-bordered small mb-0" v-if="p.seguros?.length">
            <thead class="table-dark">
              <tr>
                <th style="width:70px">Seguro</th>
                <th>Tipo de Seguro</th>
                <th style="width:55px" class="text-center">Msg</th>
                <th>Registro Cobertura</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="s in p.seguros"
                :key="s.seguro"
                :class="s.tieneCobertura ? 'table-success' : ''"
              >
                <td class="fw-bold">{{ s.seguro }}</td>
                <td>{{ s.tipoSeguro ?? '-' }}</td>
                <td class="text-center">
                  <span class="badge"
                    :class="s.mensaje === 'ok' ? 'bg-success'
                          : s.mensaje === 'no' ? 'bg-danger'
                          : 'bg-secondary'">
                    {{ s.mensaje }}
                  </span>
                </td>
                <td>
                  <span class="badge"
                    :class="s.tieneCobertura ? 'bg-success' : 'bg-danger'">
                    {{ s.registroCobertura }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="text-muted small">Sin datos de seguros.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api  from "@/services/api";
import Swal from "sweetalert2";

export default {
  name: "CCpdfValidador",

  // ── Mismos props que CVpdfValidador ──────────────────────
  props: {
    idTramite: { type: Number, required: true },
    numero:    { type: String, required: true },
    autoValidar: { type: Boolean, default: false },
  },

  emits: ['resultado'],

  data() {
    return {
      resultado:    null,
      procesando:   false,
      errorMensaje: null,
    };
  },

  computed: {
    tieneAlgunaCobertura() {
      return this.resultado?.paginas?.some(p =>
        p.seguros?.some(s => s.tieneCobertura)
      ) ?? false;
    },
  },

  mounted() {
    if (this.autoValidar) this.consultar();
  },

  methods: {
  
    async ejecutarValidacion() {
      await this.consultar();
	},  
  
    paginaTieneCobertura(pagina) {
      return pagina.seguros?.some(s => s.tieneCobertura) ?? false;
    },

    async consultar() {
      this.procesando   = true;
      this.resultado    = null;
      this.errorMensaje = null;

      try {
        // Mismo patrón de URL que CVpdfValidador
        const resp = await api.get(`/cobertura-cc/${this.idTramite}/${this.numero}`);
        const v    = resp.data;

        if (!v.cedulaEncontrada) {
          this.errorMensaje = v.mensajeError ?? "Cédula no encontrada en CC.pdf.";
          this.$emit('resultado', 'sin-cobertura');
          return;
        }

        this.resultado = v;
        this.$emit('resultado', this.calcularEstado());
      } catch (e) {
        console.error(e);
        this.$emit('resultado', 'error');
        Swal.fire({ icon: "error", title: "Error", text: "No se pudo consultar la cobertura." });
      } finally {
        this.procesando = false;
      }
    },

calcularEstado() {
  if (!this.resultado) return null;
  if (!this.resultado.cedulaEncontrada) return 'sin-archivo';

  // Buscar en todas las páginas (puede haber varias consultas del mismo paciente)
  const paginas = this.resultado.paginas ?? [];

  if (paginas.length === 0) return 'error';

  // Tomar la página más reciente (última fechaConsulta)
  const paginaMasReciente = paginas.reduce((a, b) =>
    (a.fechaConsulta ?? '') > (b.fechaConsulta ?? '') ? a : b
  );

  const seguros   = paginaMasReciente.seguros ?? [];
  const issfa     = seguros.find(s => s.seguro === 'ISSFA');
  const tieneIssfa = issfa?.tieneCobertura ?? false;

  // Error: no tiene cobertura ISSFA
  if (!tieneIssfa) return 'error';

  // Leve: nombre no coincide con BD
  const nombrePdf = paginaMasReciente.nombrePdf ?? '';
  const nombreBd  = this.resultado.nombreBd ?? '';
  const nombreCoincide = this.normalizar(nombreBd)
    .includes(this.normalizar(nombrePdf));

  if (!nombreCoincide) return 'leve';

  return 'ok';
},

normalizar(texto) {
  return texto.toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9 ]/g, '')
    .trim();
},
	
  },
};
</script>